/**
 * 任务卡 —— **全站唯一一份「任务」呈现**。
 *
 * 折叠态：缩略图 + 标题 + 状态徽章 + 一行摘要（失败时直接给错误码与原因）＋ 当前状态该有的按钮
 * 展开态：结果图集（点开就能看 / 放大 / 单张下载 / 存素材）+ 失败原因全文
 *         + 任务详情（任务号 / 模型 / 规格 / 耗时 / 消耗 / 提示词）+ 批量操作
 *
 * 三种密度，**同一份实现**（都靠 props 切，不另抄一份）：
 *   · 列表态（默认）      —— 一屏放得下十几条，缩略图 124px
 *   · `dense`             —— 顶栏那个 360px 下拉，折叠头只留标题与徽章
 *   · `forceOpen` + 抽屉   —— 「右侧展开」把这条任务摊到右边看，图更大、不离开当前页
 *
 * 点击行为有**两种**，由 `drawerOnClick` 切（默认 False = 就地展开）：
 *   · 就地展开 —— 正文接在折叠头下面，撑开列表（现在只有顶栏那个 peek 下拉用）
 *   · 右侧抽屉 —— 整条点击滑出右边那一层（历史列表用）
 *
 * ⚠️ 为什么历史列表必须是**抽屉**而不是就地展开（用户 2026-09-29 原话：
 * 「历史记录点击后是右边展开」）：
 *   1. 历史卡本身就在**右栏**里，就地展开是在一个窄列里再往下拉 —— 图只有
 *      一两百像素宽，看结果和看提示词都得来回滚，一屏还放不下两条记录。
 *   2. 「右侧展开」这个动作**原来藏在展开体的最底部**（`task__foot` 里那个
 *      `icon="expand"` 的按钮）。要先点开卡片、再滚到底、再点一次才能进宽视图 ——
 *      等于把「看这一条」拆成了三个动作，用户报障的原话就是这一点。
 *   3. 所以历史列表里**整条卡就是入口**：点哪儿都进同一层抽屉。
 *      就地展开那份实现**保留**（顶栏 peek 下拉要它），不是两套渲染 ——
 *      两种模式共用下面这一个组件、一套正文、一套操作条。
 *
 * 为什么必须合成一个组件：
 * 「点击展开」这件事此前在右栏「本页历史」里做过一遍（`CardHead` 的 `open`），
 * 但任务中心与我的作品当时还是平铺的静态行 —— 用户点了没反应，而原因永远是
 * 「另一处实现了，这一处没接」。任务卡把「这是个任务、点开有结果」变成
 * 一个组件的事，任务中心 / 本页历史 / 顶栏最近任务 / 我的作品全部复用它。
 *
 * ⚠️ 结果网格**不在本文件里重画**：走 `ResultGrid bare`。
 * 视频结果被滤掉、比例类名漂移那类事故，都是「网格有了第二份实现」造成的。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { JobOutput } from '@/api/types'
import { modelLabel } from '@/config/catalog'
import { isActive, useJobCenter, type Job } from '@/context/JobCenterContext'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { saveAllAs } from '@/lib/download'
import { fmtTime } from '@/lib/format'
import { Icon } from './icons'
import { AudioResult, ResultGrid } from './ResultGrid'
import { SmartImage } from './SmartImage'
import { Badge, Button, Card, CardBody, CardHead, CopyButton, Drawer, Progress, STATE_TONE } from './ui'

/** 错误码 → 用户该做什么。分类信息后端已经给了（`classify_error`），这里只补一句动作建议 */
const ERROR_HINT: Record<string, string> = {
  INSUFFICIENT_BALANCE: '上游余额不足：duomi 直连去 duomiapi.com 充值，走网关的模型去 songshuai.art 充值。',
  APIKEY_INVALID: '上游鉴权失败：检查 DUOMI_API_KEY / SS_ART_API_KEY 是否过期。',
  APIKEY_MISSING: '缺少上游 Key：在启动后端的终端里 export 对应密钥后重启。',
  RATE_LIMITED: '被上游限流：等一两分钟再重试即可，费用不会重复扣。',
  UPSTREAM_FAILED: '上游自己报错（系统繁忙 / 内容被拦 / 参数不被接受）。先原样重试一次；仍失败就换个模型或改一版提示词。',
  TIMEOUT: '上游超时：稍后可到「我的作品」确认是否其实已经出图。',
  UPSTREAM_UNREACHABLE: '查不到上游任务：多为上游波动，直接重试。',
  REF_UPLOAD_FAILED: '参考图没能公网化：上游拿不到你的本机图片。图床链（ImgBB → 腾讯云 COS）两家都试过了，稍后重试或检查图床配置。',
  IMG_HOST_FAILED: '公网图床都不可用（ImgBB 与腾讯云 COS）。上游只认公网地址，本机图片它取不到，所以没有「绕过图床」这一说 —— 稍后重试，或检查后端的图床配置。',
  SKILL_MISSING: '本机缺对应的 skill：检查 ~/.workbuddy/skills 下是否装了该上游技能。',
  SUBMIT_STALLED: '提交阶段卡住：上游没返回任务号，直接重试。',
  /* 下面三条 2026-10-10 补：它们早就由后端 `classify_error` 发出来了
     （库里查得到真实记录），而这里一直没有对应的「建议」—— 那三条报错
     点开之后只有一句原始 message，读不到下一步该干什么。 */
  SUBMIT_FAILED: '上游不收这次提交（多为参数组合它不支持）。按原话改一版参数、或换个引擎再试，**别原样重试**。',
  UPSTREAM_ERROR: '上游服务异常（HTTP 5xx）：多为上游自己的波动，过几分钟重试即可。',
  PARTIAL_FAILED: '这一组里有几条没成：成了的已经在结果里，没成的那几条按上面的原因处理。整单重试会连同已成功的一起重跑、并再收一次费。',
  // ⚠️ 这里曾写「不会扣费」—— **是假话**。`/cancel` 只是本地不再轮询，
  // 第三方没有取消接口（见 `PITFALLS §4.1-74`）。实测：一条被取消的 i2v 任务
  // 上游照跑完，扣了 152 币，而工作台这边 `outputs` 是空的（产出也不会再拉回来）。
  // 不许再对它做任何「不花钱」的承诺 —— 只有「已经开跑的费用照扣」是测过的。
  ABORTED: '任务已被取消，不再等它出片。注意：取消只停本地等待，拦不住上游 —— 已经开跑的费用照扣，产出也不再拉回来。',
  NETWORK: '后端不可达：确认 127.0.0.1:8000 在跑。',
}

/**
 * 错误码 → 卡上**那行字**（中文短语）。2026-10-10 加。
 *
 * ## 为什么不能直接把 `job.error.code` 印出来
 *
 * 产品经理视角自查时圈着右栏历史说「历史卡把英文错误码直接端给用户」：
 * 窄列里那一行原文是 `ABORTED · 03/12 14:20` / `UPSTREAM_FAILED · …`，
 * 配一颗没有文字的重试图标 —— 用户既看不懂发生了什么，也看不出下一步能干什么。
 * 而那行字背后其实**两种含义完全不同的事**被同一个长相表达：
 * 「我点了取消」和「上游挂了」在卡上长得一模一样。
 *
 * ## 与 `ERROR_HINT` 的分工
 *
 * `ERROR_HINT` 是**展开体里的整句建议**（「上游余额不足：去 duomiapi.com 充值」），
 * 只出现在点开之后；这里是**折叠行里的短标签**，要能塞进 250px 的摘要行。
 * 两者共用同一个 key 集合，所以下面的清单与 `ERROR_HINT` 是逐条对齐的
 * （加一个新码时两处都要加，缺了这一处只是少个中文名，不会报错 —— 所以写在注释里）。
 *
 * ## 认不出的码**原样显示**
 *
 * 后端 `classify_error` 以后新增一个码时，这里查不到 —— 那就把原码印出来。
 * 不兜底成「出错了」：那一行正好是排查时唯一的线索，
 * 而这类「有码可查」的信息比一句谁都读得懂但什么都没说的中文更值钱。
 */
const ERROR_LABEL: Record<string, string> = {
  INSUFFICIENT_BALANCE: '上游余额不足',
  APIKEY_INVALID: '上游鉴权失败',
  APIKEY_MISSING: '缺少上游 Key',
  RATE_LIMITED: '被上游限流',
  UPSTREAM_FAILED: '上游失败',
  TIMEOUT: '上游超时',
  UPSTREAM_UNREACHABLE: '上游无响应',
  REF_UPLOAD_FAILED: '参考图公网化失败',
  IMG_HOST_FAILED: '图床不可用',
  SKILL_MISSING: '本机缺 skill',
  SUBMIT_STALLED: '提交卡住',
  SUBMIT_FAILED: '提交被上游打回',
  UPSTREAM_ERROR: '上游服务异常',
  PARTIAL_FAILED: '部分失败',
  /* 「已取消」而不是「失败」：`cancel()` 把这条映射成
     `state:'failed' + error.code:'ABORTED'`（见 JobCenterContext），
     但用户看到「失败」会以为是自己做错了什么。 */
  ABORTED: '已取消',
  NETWORK: '后端不可达',
}

const errLabel = (code: string) => ERROR_LABEL[code] ?? code

const fmtElapsed = (ms: number) => {
  if (ms <= 0) return '—'
  const s = Math.round(ms / 1000)
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}s`
}

/**
 * 「右侧展开」抽屉的宽度。
 *
 * 比素材库抽屉（560）宽：那里是单选列表，这里是图集 —— 880 下按 220px 一列能排
 * 三列，一张图看得出细节；再宽就把左侧列表压没了，也就不叫「右栏展开」了。
 */
const TASK_DRAWER_WIDTH = 880

/**
 * 任务宽视图 —— 从右边滑出来的那一层。
 *
 * 为什么是抽屉，而不是新标签页 / 独立详情页：
 *   · 用户的原话是「你就不能弹窗或者右边展开吗」。开新标签页会把当前列表的
 *     滚动位置、筛选状态、展开态全留在身后那一个标签里，看完还得切回来找 ——
 *     这是**任务附属信息**，不该单独占一个窗口；
 *   · 也不是「弹窗居中」：图集是宽内容，居中弹窗要么图小要么左右留白，
 *     抽屉贴边滑出能拿满 880px，和项目里素材库那套右栏也是同一个视觉语言。
 *
 * ⚠️ 里面**不重画任务**：仍然是 `TaskCard` 自己，只是 `forceOpen` 常摊。
 * 于是「结果图集 / 失败原因 / 任务详情 / 批量操作」永远只有一份实现 ——
 * 这正是之前「详情页抄了一份任务渲染」那类问题的根治法。
 *
 * portal 到 body：任务卡常年挂在 `.card` 里，而卡片为了缩略图圆角裁切是
 * `overflow: hidden` 的。抽屉作为卡片的后代节点会被裁掉，只能挂在 body 上
 * （图片放大遮罩 ImageLightbox 同一个理由）。
 */
function TaskDrawer({
  job,
  aspectRatio,
  itemActions,
  actions,
  restore,
  onRedo,
  open,
  onClose,
}: {
  job: Job
  aspectRatio?: string
  itemActions?: (o: JobOutput, i: number) => ReactNode
  actions?: ReactNode
  /** 「重做」入口 —— 与 `restore` 同理必须跟着转发进来（折叠头那颗按钮在抽屉里才看得到） */
  onRedo?: (job: Job) => void
  /**
   * 恢复入口**必须跟着转发进来**。
   *
   * `drawerOnClick` 模式下正文永不就地摊开（`bodyOpen = open && !drawerMode`），
   * 那颗按钮只存在于抽屉里这张 `forceOpen` 的卡上 —— 这里漏转发，用户在
   * 「已清理」列表点开一条记录，看到的还是「移除记录」（一个点了没反应的按钮）。
   * 这类「入口挂在不渲染的那一层」本项目已经踩过（抽屉里的卡片又挂抽屉）。
   */
  restore?: (id: string) => void
  open: boolean
  onClose: () => void
}) {
  // dense 不放批量按钮是为了 360px 的顶栏下拉；抽屉里宽度够，一律给全
  const { dismiss } = useJobCenter()

  if (!open) return null

  return createPortal(
    <Drawer
      open
      onClose={onClose}
      icon="expand"
      width={TASK_DRAWER_WIDTH}
      title={
        <span className="row" style={{ gap: 8, alignItems: 'center' }}>
          任务详情
          <Badge tone={STATE_TONE[job.state]?.tone ?? 'neutral'}>
            {STATE_TONE[job.state]?.label ?? job.state}
          </Badge>
        </span>
      }
      className="task-drawer"
    >
      <TaskCard
        job={job}
        forceOpen
        inDrawer
        aspectRatio={aspectRatio}
        itemActions={itemActions}
        actions={actions}
        restore={restore}
        onRedo={onRedo}
        // 在抽屉里点了「移除记录」，抽屉必须跟着关 —— 任务已经从列表里没了，
        // 留着一层空抽屉只会让人以为界面卡住
        onDismiss={(id) => {
          dismiss(id)
          onClose()
        }}
      />
    </Drawer>,
    document.body,
  )
}

export function TaskCard({
  job,
  onCancel,
  onRetry,
  onDismiss,
  onRedo,
  headerExtra,
  actions,
  aspectRatio,
  defaultOpen,
  onOpenChange,
  dense,
  itemActions,
  forceOpen,
  inDrawer,
  drawerOnClick,
  restore,
  onPlayAudio,
  lean,
  also,
}: {
  job: Job
  /**
   * **同一次提交里跟着这条一起发出去的附带任务**（`Job.attachedTo` 指向本任务）。
   *
   * 2026-10-09 用户口径：「这个文案跟图片还是同一个任务比较好吧，分开点开很奇怪」
   * —— `/social-image` 点一次「生成」发的是**两条独立任务**（图 + 配套文案），
   * 右栏于是摆出两张卡、各点一次才能看全。用户选的是**只合并展示层**，
   * 所以这里只是把附带任务的**进度与产出并进这张卡**：
   *   · 摘要区多出它们各自的一行（任务名 + 进度），一眼看得见都在跑；
   *   · 展开体里，产出照旧走下面那三段（媒体 / 文本 / 音频），只是喂进去的
   *     列表是**整组**的；哪段文案是谁写的，看段头那行归属。
   *
   * 🔴 **两条任务本身一个字节都没合并**：各自计费、各自成败、各自重试、
   * 各自在「本页历史」里占一条。别顺手把它们的 `outputs` 写进主任务的 job 里
   * —— 那会让「这条任务花了多少钱、出了几个结果」变成一道没有答案的题
   * （`isResultJob` / 计价 / 历史都按单条任务算）。
   *
   * ⚠️ 调用方必须保证**第一条是主任务**（分组由 `studio.tsx` 的 `groupJobs`
   * 按 `attachedTo` 聚，本组件不猜谁是谁）。不传 = 与改前完全一样的一张卡。
   */
  also?: Job[]
  onCancel?: (id: string) => void
  onRetry?: (id: string) => void
  onDismiss?: (id: string) => void
  /**
   * **「重做」** —— 把这条任务的参数填回**当前页的表单**（2026-10-09 用户口径：
   * 「历史记录那边加一个按钮，可以点击重做一次，然后把参数都输入回去」）。
   *
   * ⚠️ 它**不是** `onRetry`：重试是「原样再跑一遍」，重做只是**把参数填回左栏**，
   * 由用户核对 / 改动后再点生成。两者都留着：一个是「再来一次」，一个是
   * 「在这基础上改一版」。
   *
   * 判据落在**主任务自己**（`job`）而不是整组：参数（`inputs`）是主任务那一份，
   * 附带任务（配套文案）没有独立表单可填。不传 = 不出现这颗按钮（任务中心 /
   * 我的作品这些**没有左栏表单**的地方就不该有它 —— 填回去也没地方落）。
   */
  onRedo?: (job: Job) => void
  /** 折叠头右侧的页面专属控件（如分类下拉）。内部已 `stopPropagation`，不会误触展开 */
  headerExtra?: ReactNode
  /** 展开体底部的页面专属操作（如「删除这件」） */
  actions?: ReactNode
  aspectRatio?: string
  /** 默认是否展开（失败任务建议默认展开，原因一眼可见） */
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * 紧凑模式：折叠头只留标题 + 状态徽章 + 展开箭头，不放批量操作按钮。
   * 给顶栏那个 360px 的下拉用 —— 一排「取消 / 下载全部 / 移除」在那个宽度里会挤成两行。
   * 展开体里的操作照旧，所以**不是「少了一套实现」，只是同一份实现了两种密度**。
   */
  dense?: boolean
  /** 单张产出上的页面专属操作（「我的作品」用它做「删除这一张」） */
  itemActions?: (o: JobOutput, i: number) => ReactNode
  /**
   * 宽视图专用：**强制摊开正文**，也**不再可折叠**（折叠头不挂 onToggle、不显示箭头）。
   *
   * 列表里那套「没东西可看就不给展开」是为了省地方；抽屉就是为「仔仔细细看这一条」
   * 而存在的 —— 哪怕它没有产出，也要把「这个任务没有产出」连同模型 / 规格 /
   * 来源 / 提示词一起摊给用户，而不是给一张点不开的卡片。
   */
  forceOpen?: boolean
  /**
   * 「我正被渲染在右侧抽屉里」—— 于是不再挂「右侧展开」按钮。
   *
   * 靠 prop 而不是靠路由判断（上一版是拿 `pathname === /jobs/:id` 判断的）：
   * 抽屉是叠在**同一个页面**上的一层，地址栏根本不变，路由判断认不出来。
   */
  inDrawer?: boolean
  /**
   * **整条点击 = 在右侧展开**（历史列表专用，见文件头结论 3）。
   *
   * 开了它之后：正文不再就地摊开，折叠头本身成为抽屉入口，箭头朝右表示「往外开」。
   * 历史列表是**回看**场景 —— 用户已经知道要看哪一条，缺的只是「怎么看大」。
   *
   * `forceOpen`（抽屉内部那张卡）与它互斥语义：`forceOpen` 是「已经在宽视图里」，
   * `drawerOnClick` 是「点一下进宽视图」。两个都传时前者优先，不会自己套自己。
   */
  /**
   * 「恢复记录」入口 —— **只有「已清理」视图传它**。
   *
   * 传了之后，展开体底部那颗按钮从「移除记录」换成「恢复记录」：
   * 给一条**已经清理过**的记录再摆一个「移除」按钮，点了什么都不会发生
   * （后端幂等，改动 0 行），用户只会以为按钮坏了。
   *
   * 恢复本身不重建任务、不重跑、不花钱 —— 库里那行一直在，只是把 `hidden` 置回 0。
   */
  restore?: (id: string) => void
  drawerOnClick?: boolean
  /**
   * 「这条任务有能播的音频」时的回调 —— 传了才在折叠头出现一颗播放键。
   *
   * ⚠️ **判据在组件内部**（这条任务真有 `type === 'audio'` 的产出），不由调用方筛：
   * 让调用方判「什么算能播」，就是把同一条规则抄到每个调用点上，
   * 迟早出现「这个页面给按钮、那个页面不给」——本项目对这类漂移有前科（历史块曾经 4/10 页）。
   *
   * `dense` 下不渲染：那是顶栏 360px 的下拉，一排「播放 / 取消 / 徽章」会挤成两行。
   */
  onPlayAudio?: (o: JobOutput) => void
  /**
   * `lean` —— **紧凑列表行**（2026-10-08 用户口径「这种简约」「字体间距大力模仿」）。
   *
   * 给右栏那栏用（`StudioPanel` 的历史列表）。与默认密度的差别只有三处，
   * 全部是**减法**，没有砍掉任何信息入口：
   *
   *   ① 提示区**只留一行**。默认那两行（`N 个结果 · ¥0.05 · 耗时 1s` /
   *      `10/08 18:06 · minimax-tts · 历史 · 点击在右侧展开`）在任务中心那种
   *      1000px 宽的整页列表里是对的 —— 一屏十几条、扫过去就够。但参考站的
   *      右栏列表**一条只有两行**（标题 + 一行灰色副标题），第三行开始就是噪音。
   *      合并规则（2026-10-09 再砍一刀，用户口径「废话太多」）：
   *      **「结果数 + 时间」**——`1 个结果 · 10/09 11:05`。
   *      模型名 / 来源页 / 「点击展开」提示全部去掉（整条可点是已知约定，
   *      模型名点开就在抽屉里第一行）；**花费与耗时也去掉** ——
   *      一屏十条里它们条条相同，读完等于没读，要核对点开抽屉就有。
   *   ② 状态动作**只给图标**。默认是「▶ 播放」这种带字按钮，在 250px 的右栏里
   *      三个就折行；换成 28px 的图标键，`title` / `aria-label` 一个不少。
   *   ③ 内边距与行距收紧（见 global.css `.task--lean`）。
   *
   * ⚠️ **它不是 `dense` 的第二份实现**，两个挡位的取舍完全不同：
   * `dense` 是「**别把面板撑开**」（顶栏 360px 下拉，连徽章与展开提示都要省），
   * `lean` 是「**让列表读起来清爽**」（徽章留着、整条可点也留着，只是不铺三行小字）。
   * 所以 `lean` 与 `dense` 同时传时，以 `lean` 的排版为准（`dense` 的省略规则
   * 内部仍然生效 —— 见下面 `dense` 分支，它们不冲突）。
   *
   * ⚠️ 只有 `drawerOnClick`（整条点开抽屉）那一档才用它。散装列表（任务中心 /
   * 我的作品）里一条记录要承担「读错误码 + 找重试」两个任务，两行不够。
   */
  lean?: boolean
}) {
  const { addGenerated } = useAssetLibrary()
  const toast = useToast()
  /**
   * 取消 / 重试 / 移除默认从任务中心取，**不要求每个调用方再传一遍**。
   *
   * 任务卡被 4 处复用（顶栏最近任务 / 生成页本页历史 / 任务中心 / 我的作品）。
   * 若这三个动作靠 props 透传，每加一处调用就得把 10 个生成页的 prop 链再抄一次
   * —— 「同一件事在两处各写一遍」，正是本项目反复复发的那类 bug。
   * 仍保留同名 props 作为覆盖入口。
   */
  const center = useJobCenter()
  const cancel = onCancel ?? center.cancel
  const retry = onRetry ?? center.retry
  const dismiss = onDismiss ?? center.dismiss
  /**
   * 失败任务**默认展开**：用户的原话是「失败原因也要记录阿，不然怎么知道」。
   * 折叠态只给一行省略号，真正的原因全文在展开体里，所以失败就该自动摊开。
   *
   * ⚠️ `dense`（顶栏那个 460px 下拉）是**唯一例外**（2026-09-30 用户报「任务中心 UI 有问题」）：
   * 那个面板是「扫一眼现在在跑什么」的场景，一条失败记录的正文（上游原话里常带两串 UUID）
   * 能吃掉半个滚动区 —— 实测 8 条卡只看得见 5 条。折叠头那行已经印了错误码，
   * 要看全文就点开（进右侧抽屉）或去「任务中心」页。**不是「dense 里不给失败信息」**，
   * 是不在这个尺寸里摊开。
   */
  const autoOpenFailed = job.state === 'failed' && !dense
  const [open, setOpen] = useState(defaultOpen ?? forceOpen ?? autoOpenFailed)
  const [busy, setBusy] = useState(false)
  const wasFailed = useRef(job.state === 'failed')

  /**
   * 「右侧展开」抽屉的开关。
   *
   * 声明在这里（而不是原来靠近 return 的地方）：`toggle` 要用它 ——
   * 抽屉模式下整条点击就是开抽屉。放在 `toggle` 后面会撞上 TDZ。
   */
  const [detailOpen, setDetailOpen] = useState(false)

  /** 抽屉模式下正文**永不就地摊开** —— 展开的去处是右边那一层，不是这儿 */
  const drawerMode = !!drawerOnClick && !forceOpen
  const bodyOpen = open && !drawerMode

  // 任务是在「进行中 → 失败」之间**动态**变的：初始 state 只反映了挂载那一刻。
  // 没有这个 effect，用户盯着看的任务失败后卡片还是收着的，得自己点一下才知道错了。
  // 抽屉模式下不必自动摊正文（点了就进抽屉，折叠头那行也已经印了错误码+原因），
  // 但 `wasFailed` 仍要推进，否则切回就地展开时会被当成新失败再摊一次。
  // `dense` 同理（见 autoOpenFailed 的注释）：不摊开，但标记要推进。
  useEffect(() => {
    if (!wasFailed.current && job.state === 'failed' && job.error) {
      wasFailed.current = true
      if (!drawerMode && !dense) setOpen(true)
    }
  }, [job.state, job.error, drawerMode, dense])

  /**
   * 这一组任务 = **主任务 + 附带任务**（`also`，见 prop 上的说明）。
   * 没传时它就是 `[job]`，下面所有「整组」的写法都退化成改前的行为。
   */
  const group = useMemo(() => [job, ...(also ?? [])], [job, also])
  /** 这一组还在跑（主任务或任一条附带任务）—— 「还在跑」是**整张卡**的性质 */
  const active = group.some((j) => isActive(j.state))
  /**
   * 徽章按**还在跑的那一条**报，都不在跑就按**挂掉的那一条**报。
   *
   * 不这样写的话：主任务先跑完、附带任务还在跑时，徽章写「已完成」而卡上还有
   * 一条进度条 —— 同一张卡上两处说法互相打架（60 秒的图先回来、12 秒的文案
   * 后回来是常规，反过来的顺序也可能是某条提交失败得早）。
   * 同理，主任务成、附带任务挂，徽章还写「已完成」的话那张失败就被埋进摘要行里了。
   */
  const stateJob =
    group.find((j) => isActive(j.state)) ?? group.find((j) => j.state === 'failed') ?? job
  const tone = STATE_TONE[stateJob.state] ?? STATE_TONE.queued
  const elapsed = (job.finishedAt ?? Date.now()) - job.createdAt
  const remain = Math.max(0, Math.round((job.estimatedMs - elapsed) / 1000))
  /** 整组全部产出（主任务在前）—— 缩略图上的数字、下载全部、存素材都以它为范围 */
  const allOutputs = useMemo(() => group.flatMap((j) => j.outputs), [group])

  /*
   * 🔴 产出按形态**分三份**，`media` 只留 image / video。
   *
   * 原来是 `filter(o => o.type !== 'text')`，于是音频落进 `media`，再被交给
   * `ResultGrid` —— 而那个网格只认 image / video，音频**被静默滤掉**。
   * 症状（2026-10-09 用户报「任务详情要注意，音频类还是要可以播放才可以」）：
   * 展开体里写着「共 1 个结果 · 点击任意一张放大」，下面**空无一物**，
   * 也没有任何播放器可点 —— 产物其实好好的，只是没人渲染它。
   * 这与 `ResultGrid` 头注里记着的那次「视频结果被滤掉」是同一个病：
   * 分形态的三张卡各管一段，喂错卡就等于没渲染。
   *
   * ⚠️ 2026-10-09（图文同出）：三份都改成从**整组**筛，不是只筛 `job.outputs`。
   * 筛法一个字没动，喂进去的列表长了 —— 一张卡里装两条任务时，附带任务
   * （文案）的文本产出必须走同一条路渲染，否则就是上面那个老病的新发型。
   */
  const media = useMemo(
    () => allOutputs.filter((o) => o.type === 'image' || o.type === 'video'),
    [allOutputs],
  )
  /**
   * 文本产出连带**它的归属任务**。
   *
   * 为什么不是裸的 `JobOutput[]`：一张卡里两条任务时，段头只写「文案 1」
   * 就看不出这段是谁写的（主任务也可能有文本产出）。段头用归属任务的真名，
   * 于是「这段属于图还是属于配套文案」不用猜 —— 单任务时仍写「文案 1」，
   * 与改前逐字一致。
   */
  const texts = useMemo(
    () =>
      group.flatMap((owner) =>
        owner.outputs.filter((o) => o.type === 'text' && o.text).map((o) => ({ o, owner })),
      ),
    [group],
  )
  /** 音频产出（语音合成）。走 `AudioResult` —— 它自带原生播放器、下载与存素材 */
  const audios = useMemo(
    () => allOutputs.filter((o) => o.type === 'audio' && o.url),
    [allOutputs],
  )
  /** 这一组里第一条能播的音频（`onPlayAudio` 传了才有用，见 prop 注释） */
  const audioOut = useMemo(
    () => allOutputs.find((o) => o.type === 'audio' && o.url),
    [allOutputs],
  )

  /** 有东西可看才允许展开 —— 空结果的进行中任务点开只有进度，不值得当折叠头。
   *  `forceOpen`（详情页）例外：那里正文常摊，也就没有「能不能展开」这回事。
   *  `drawerOnClick`（历史列表）也例外：**点得开**，因为抽屉对零产出的任务照样给
   *  模型/规格/来源/提示词 —— 那是回看失败最需要的东西。 */
  const canExpand =
    !!forceOpen ||
    media.length > 0 ||
    texts.length > 0 ||
    audios.length > 0 ||
    group.some((j) => !!j.error) ||
    !!job.prompt

  const toggle = useCallback(() => {
    // 抽屉模式：整条卡 = 抽屉入口，与「有没有产出」无关（见 canExpand 的注释）
    if (drawerMode) {
      setDetailOpen(true)
      return
    }
    if (!canExpand) return
    setOpen((v) => {
      onOpenChange?.(!v)
      return !v
    })
  }, [canExpand, onOpenChange, drawerMode])

  /**
   * 只有产出是**这个任务自己的**时才给批量操作入口，否则会诱导用户以为「下载全部」是下载整页。
   *
   * ⚠️ 一条卡里装了主任务 + 附带任务时，这里收的是**整张卡看得见的那些产出**
   * （`allOutputs`）—— 屏幕上摊着图加文案、按钮却只存图，那才是真会让人误会的那种
   * 「说的和做的不一致」。资产 id 仍用主任务 id 打头：一次提交只可能有一组，
   * 而主任务是这一组里唯一必然存在的那个。
   */
  const saveAllToLibrary = useCallback(() => {
    if (!allOutputs.length) return
    allOutputs.forEach((o, i) => {
      if (o.type === 'text' && !o.text) return
      addGenerated({
        id: `asset-${job.id}-${i}`,
        url: o.url,
        thumbUrl: o.thumbUrl ?? o.url,
        name: `${job.title}${allOutputs.length > 1 ? ` #${i + 1}` : ''}`,
        kind: o.type,
        createdAt: new Date().toISOString(),
        source: 'generated',
      })
    })
    toast.success(`已存入参考图素材库 ${allOutputs.length} 个`)
  }, [addGenerated, allOutputs, job, toast])

  const downloadAll = useCallback(async () => {
    const list = allOutputs
      .filter((o) => o.url)
      .map((o, i) => ({
        url: o.url,
        name: `${job.title}${allOutputs.length > 1 ? `_${i + 1}` : ''}`,
      }))
    setBusy(true)
    await saveAllAs(list)
    setBusy(false)
    toast.success(`已开始下载 ${list.length} 个文件`)
  }, [allOutputs, job, toast])

  const thumb = media[0]?.thumbUrl ?? media[0]?.url ?? texts[0]?.o.url

  /** 整组里有没有挂掉的（缩略图那个警示图标 / 「这个任务没有产出」都要看整组） */
  const hasError = group.some((j) => !!j.error)

  /**
   * 取消**整组里还在跑的那些**。
   *
   * 只取消主任务的话：卡没了、文案还在跑（而且照旧扣费），而界面上再没有
   * 任何地方能停掉它 —— 用户点一次「生成」发出去的两条，就该由同一次点击收回来。
   * 已经跑完 / 已经失败的**不动**（`cancel` 对它们本来就是空转，这里显式过滤
   * 是为了不把「取消」的作用范围说成整组）。
   */
  const cancelGroup = () => group.filter((j) => isActive(j.state)).forEach((j) => cancel(j.id))

  /**
   * 一张卡里两条任务时的**合并标题**：把各自 `·` 前那一段并起来
   * —— `朋友圈配图 + 朋友圈文案 · INS风格的可穿戴音箱`。
   *
   * 用户口径（2026-10-09）是「文案跟图片还是同一个任务比较好吧」，所以卡上
   * 得看得出这一张卡同时管着两件事；只印主任务的名字，读起来还是「这是图的任务，
   * 文案不知从哪冒出来的」。
   *
   * ⚠️ 拼不出**读得通**的一句时**原样退回主任务标题**，不硬拼：
   *   · 任一条的标题里没有 `·`（用户自填的标题、或档位改了命名）；
   *   · 各条的 `·` **后半段不一致**（不是同一个产品）——
   *     这时拼出来的是「A + B · 某产品」，而那两件事跟那个产品没关系。
   * 少说一句只是不好看，拼错一句是**假话**（本项目对这两种代价的取舍一贯如此）。
   */
  const groupTitle = useMemo(() => {
    if (group.length < 2) return job.title
    const parts = group.map((j) => {
      const at = j.title.indexOf('·')
      return at > 0
        ? { head: j.title.slice(0, at).trim(), tail: j.title.slice(at + 1).trim() }
        : null
    })
    if (parts.some((p) => !p)) return job.title
    const tail = parts[0]!.tail
    if (parts.some((p) => p!.tail !== tail)) return job.title
    return `${parts.map((p) => p!.head).join(' + ')} · ${tail}`
  }, [group, job.title])

  /**
   * 一条任务的**短状态**（附带任务在同一张卡里的那一行用）。
   *
   * 与行 A 是同一套说法，只是不带「预计剩余」：那一格是主任务的，
   * 而这一行的重点是「它还在不在跑、跑到哪了」——两个「预计剩余 2s / 45s」
   * 并排只会让人以为是一个任务的两个进度。
   */
  const shortState = (j: Job) =>
    j.error ? (
      <span className="task__err-line">{errLabel(j.error.code)}</span>
    ) : isActive(j.state) ? (
      <span>{j.state === 'queued' ? '排队中' : `生成中 ${j.progress.toFixed(0)}%`}</span>
    ) : (
      <span>{j.outputs.length ? `${j.outputs.length} 个结果` : '没有产出'}</span>
    )

  return (
    <>
      {/* `data-click` 是给验收探针的锚点：点击行为有抽屉/就地两种，
          只看 DOM 没法区分「这条点了会开抽屉」和「这条点了会往下撑」。
          探针认 `data-*` 而不是类名 —— 本项目吃够了「选择器改名恒读空串」的亏。 */}
      <Card
        className={`task${lean ? ' task--lean' : ''}${bodyOpen ? '' : ' task--folded'}`}
        data-state={job.state}
        data-click={drawerMode ? 'drawer' : 'inline'}
        data-density={lean ? 'lean' : dense ? 'dense' : 'default'}
      >
      <CardHead
        lead={
          <span className="task__thumb">
            {thumb ? (
              <SmartImage src={thumb} alt="" kind={media.length ? 'image' : 'text'} />
            ) : (
              <Icon
                name={
                  hasError
                    ? 'alert'
                    : active
                      ? 'bolt'
                      : /* 只有音频的任务（语音合成）给一个声波图标 ——
                           原来一律走下面那条「视频/图片」二选一，于是音频任务的
                           缩略图格子里画着一个图片图标，与产出完全不相干。 */
                        audios.length && !media.length
                        ? 'wave'
                        : job.appSlug.includes('video')
                          ? 'video'
                          : 'image'
                }
                size={16}
              />
            )}
            {allOutputs.length > 1 ? <b className="task__count">{allOutputs.length}</b> : null}
          </span>
        }
        title={<span className="task__title">{groupTitle}</span>}
        hint={
          /*
           * 🔴 提示区是**两个显式的行**，不是一串让它自己折行的内联片段。
           *
           * 原来这里是 6 个字段 + 5 个分隔符平铺在一条 flex 流里，靠 `flex-wrap` 兜底。
           * 在宽卡里（任务中心整页）一行放得下，看不出问题；一进右栏（本页历史，
           * 卡片只有 300px 宽、留给提示区的那一列不到 90px）就逐项折行 ——
           * 2026-10-08 用户圈着整块报「弄正常点，2 行的东西」，截图里那 6 个字段
           * 折成了 8 行，而且折出来的行首挂着孤零零的 `·`。
           *
           * 两行是**语义**分出来的，不是宽度算出来的：
           *   行 A = 这次跑得怎么样（结果数 / 花费 / 耗时，或进行中进度，或错误码）
           *   行 B = 这是什么、什么时候跑的（时间 / 模型 / 来源页 / 历史标记 / 展开提示）
           * 分工写在结构里，就不存在「窄了以后哪两个字段挤在一起」这种随机结果。
           *
           * 行内仍然允许 `flex-wrap`（一行放不下就折，不会溢出），但**跨行不会混**。
           *
           * ⚠️ `lean`（右栏列表行）**整块换掉**：只留一行「结果数 + 时间」，
           * 见下面那一支与 `lean` prop 上的注释。不是「把两行 CSS 藏一行」——
           * 是把字段真的合并/裁掉（模型名、来源页、花费、耗时都不进这一行）。
           */
          lean ? (
            <span className="task__hint task__hint--lean">
              {/*
                * 这里读的是 `stateJob` / `allOutputs` 而不是裸的 `job.*`：
                * 一整组的卡（`also`）进来时，「还在跑的是哪一条」与「一共几个结果」
                * 都得按**整组**答，否则主任务先跑完、文案还在写的那一瞬间
                * 这一行会印出「生成中 100%」这种自相矛盾的话。
                * 单任务时这两个变量就是 `job` 本身，与改前逐字一致。
                */}
              {job.error ? (
                <span className="task__err-line">{errLabel(job.error.code)}</span>
              ) : active ? (
                <span>
                  {stateJob.state === 'queued' ? '排队中' : `生成中 ${stateJob.progress.toFixed(0)}%`}
                </span>
              ) : (
                <span>{allOutputs.length ? `${allOutputs.length} 个结果` : '没有产出'}</span>
              )}
              {/* 时间只有一格 `MM/DD HH:mm`。**保留它**：右栏是回看场景，
                  「刚才跑的那条」与「前天那条」必须一眼分得开，而这正是
                  参考站那条副标题（`复古, 合成器贝斯, R&B`）占的位置。

                  ⚠️ 花费（`¥0.05`）与耗时（`耗时 1s`）**2026-10-09 砍掉**
                  （用户口径「废话太多」）：一屏十条里它们条条相同，读完等于没读；
                  要核对花费/耗时点开抽屉第一屏就有（「消耗」「耗时」两行）。 */}
              <span className="task__sep">·</span>
              <span>{fmtTime(job.createdAt)}</span>
            </span>
          ) : (
          <span className="task__hint">
            <span className="task__hint-row">
              {job.error ? (
                <span className="task__err-line">
                  {/* dense 只印错误码：上游原话里常常带两串 UUID（实测一条能占两行），
                      在 460px 的下拉里是纯噪音，点开这条就有全文。
                      ⚠️ 码用中文短语（`errLabel`）—— 这里与右栏历史是**同一行文案**，
                      两处各印一种语言的话，「已取消」与「ABORTED」会像是两件事。 */}
                  {errLabel(job.error.code)}
                  {dense ? null : ` · ${job.error.message}`}
                </span>
              ) : isActive(job.state) ? (
                <span>
                  {job.state === 'queued' ? '排队中，等待上游空闲' : `生成中 ${job.progress.toFixed(0)}%`}
                  {remain > 0 ? ` · 预计剩余 ${remain}s` : ' · 即将完成'}
                </span>
              ) : (
                <span>
                  {job.outputs.length ? `${job.outputs.length} 个结果` : '没有产出'}
                  {job.cost ? ` · ¥${job.cost.toFixed(2)}` : ''}
                  {/* 耗时只有真跑过的任务才有（作品图集没有这一项），
                      没有就不印 —— 一屏十几个「耗时 —」是纯噪音 */}
                  {elapsed > 0 ? ` · 耗时 ${fmtElapsed(elapsed)}` : ''}
                </span>
              )}
            </span>

            {/*
              * 同一次提交里的**附带任务**（`/social-image` 的配套文案）各占一行。
              *
              * 为什么必须有这几行：一张卡里两条任务、只印主任务那一行的后果是
              * 「文案还在不在跑、跑到哪了」在卡上**没有任何位置** —— 而它照样占着
              * 上游、照样计费。行 A 报的是**主任务自己**的状态（`isActive(job.state)`，
              * 不是整组的 `active`）：主任务先回来、文案还在写是常规顺序，
              * 两行各说各的那一条，读起来才是「两件事在跑」，而不是「一个任务两个进度」。
              *
              * ⚠️ 任务名用**它自己的真名**，不做「取 `·` 前那一段」这类缩写：
              * 姓名是用户认得出的锚点，截断交给 CSS（`.task__hint-name`）。
              * 主任务那一行的位置不动，所以单任务（不传 `also`）的卡与改前逐字一致。
              *
              * ⚠️ 这几行走 `task__hint-peer` 而**不是** `task__hint-row`：
              * 行 A / 行 B 是**同一条任务的两组字段**，这里是一行一条任务，
              * 不是同一件事。`smoke-nav.ts` 有一条断言逐字数「`task__hint-row`
              * 恰好两个」，守的是「别退回把 6 个字段平铺成一条流」那件事 ——
              * 借它的类名把行数顶上去，等于给那条断言开了个口子，
              * 下次真退回平铺时它也认不出来。布局规则两处共用（见 global.css），
              * 所以这不是「两份实现」。
              */}
            {group.slice(1).map((j) => (
              <span className="task__hint-peer" key={j.id}>
                <span className="task__hint-name" title={j.title}>
                  {j.title}
                </span>
                <span className="task__sep">·</span>
                {shortState(j)}
              </span>
            ))}

            {/*
              ⚠️ dense（顶栏那个 360px 下拉）**行 B 到此为止**：模型名 / 来源页 /
              「历史」全部不渲染 —— 那个面板的定位是「扫一眼现在在跑什么」，
              塞进来必然折成三行小字（2026-09-30 用户圈着整个面板报「任务中心 UI 有问题」）。
              要这些字段就点开这条进右侧抽屉，或去整页的「任务中心」。
            */}
            <span className="task__hint-row">
              {fmtTime(job.createdAt)}
              {dense ? null : (
                <>
                  <span className="task__sep">·</span>
                  {job.modelName ?? modelLabel(job.model)}
                  {job.sourcePage ? (
                    <>
                      <span className="task__sep">·</span>
                      {/* 来源页在窄卡里由 CSS 收起（见 global.css 的 `.task__page`）：
                          右栏「本页历史」印的必然就是本页，这一个词是纯冗余 */}
                      <span className="task__page">{job.sourcePage}</span>
                    </>
                  ) : null}
                  {job.remote ? (
                    <>
                      <span className="task__sep">·</span>
                      <span title="从后端任务库读回的历史记录">历史</span>
                    </>
                  ) : null}
                </>
              )}
              {!bodyOpen && (canExpand || drawerMode) ? (
                <>
                  <span className="task__sep">·</span>
                  {/* 文案都压到 4 字以内：这一行末尾就是卡片右缘，多一个字都可能把
                      「模型名 + 历史 + 提示」整行挤折（2026-10-08 实测：
                      「点击在右侧展开」比「右侧展开」多占 36px，模型名一长就折出第三行）。
                      方向由旁边的 `>` 箭头表达，「点击」两字是多余的。 */}
                  <span className="task__cue">
                    {drawerMode ? '右侧展开' : dense ? '展开' : '展开看结果'}
                  </span>
                </>
              ) : null}
            </span>
          </span>
          )
        }
        open={bodyOpen}
        onToggle={!forceOpen && (drawerMode || canExpand) ? toggle : undefined}
        right={
          /*
           * 折叠头只放**状态动作**（取消 / 重试）与徽章 —— 它们是这一行当下唯一该做的事。
           * 产出类动作（下载全部 / 存素材 / 移除记录）放在展开体的操作条里：
           * 一是「看结果」和「处理结果」本就该挨着，二是顶栏那个 360px 的下拉
           * 塞不下四个按钮（会挤成两行）。
           */
          <span className="task__acts" onClick={(e) => e.stopPropagation()}>
            {headerExtra}
            {/*
              ⚠️ `lean` 下**不画状态徽章**。
              理由：右栏历史里 95% 的行是「已完成」，一列十个一模一样的绿字徽章
              读完等于没读（参考站那两行列表里也没有任何状态位）。
              需要用户注意的状态**没有被藏掉** —— 进行中/排队显示在摘要行
              （`生成中 42%`），失败显示错误码，两者都由 `.task__err-line` /
              常驻的 `.spinner` 表达；`dense` 那个 360px 下拉里也一样只留一行。
              真相永远在展开体（点开就是抽屉）的「状态」与失败原因里。
            */}
            {lean ? null : <Badge tone={tone.tone}>{tone.label}</Badge>}
            {onPlayAudio && audioOut && !dense ? (
              lean ? (
                <Button
                  size="icon"
                  variant="ghost"
                  icon="play"
                  title="在底部试听条里播放这条音频"
                  aria-label={`播放：${job.title}`}
                  onClick={() => onPlayAudio(audioOut)}
                />
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  icon="play"
                  title="在底部试听条里播放这条音频"
                  onClick={() => onPlayAudio(audioOut)}
                >
                  播放
                </Button>
              )
            ) : null}
            {active && !dense ? (
              lean ? (
                <Button
                  size="icon"
                  variant="ghost"
                  icon="x"
                  title={
                    group.length > 1
                      ? '取消这一组任务（图文一起发出去的，只停本机等待，上游已开跑的费用照扣）'
                      : '取消这个任务（只停本机等待，上游已开跑的费用照扣）'
                  }
                  aria-label={`取消：${job.title}`}
                  onClick={cancelGroup}
                />
              ) : (
                <Button size="sm" variant="outline" onClick={cancelGroup}>
                  {group.length > 1 ? '取消整组' : '取消'}
                </Button>
              )
            ) : null}
            {job.state === 'failed' && !dense ? (
              <Button
                size={lean ? 'icon' : 'sm'}
                variant={lean ? 'ghost' : 'outline'}
                icon="refresh"
                title="按原参数重试"
                aria-label={lean ? `重试：${job.title}` : undefined}
                onClick={() => void retry(job.id)}
              >
                {lean ? null : '重试'}
              </Button>
            ) : null}
            {/*
              「重做」—— 把参数**填回左栏表单**，不是再跑一遍（那是上面那颗「重试」）。
              只给**已结束**的任务（还在跑的没有完整参数）；`dense`（顶栏 360px 下拉）
              不给，理由同「重试」：那一排塞不下第二个按钮。
              ⚠️ 判据用主任务 `job`（`inputs` 是它的），与整组无关。
            */}
            {onRedo && !dense && !isActive(job.state) ? (
              lean ? (
                <Button
                  size="icon"
                  variant="ghost"
                  icon="edit"
                  title="重做：把这次任务的参数填回左侧表单，改完再点生成"
                  aria-label={`重做：${job.title}`}
                  onClick={() => onRedo(job)}
                />
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  icon="edit"
                  title="把这次任务的参数填回左侧表单，改完再点生成"
                  onClick={() => onRedo(job)}
                >
                  重做
                </Button>
              )
            ) : null}
          </span>
        }
      />

      {bodyOpen && canExpand ? (
        <CardBody tight>
          <div className="task__body">
            {/*
              * 进度条**一条任务一条**（只跑一条时就是原来那一根）。
              *
              * 一张卡里两条任务时，光两根条子并排是读不懂的（谁是谁？），
              * 所以只有成组时在前面印一行任务名 —— 单任务那一支一个像素没变。
              */}
            {group
              .filter((j) => isActive(j.state))
              .map((j) => (
                <div key={j.id} style={{ marginBottom: 12 }}>
                  {group.length > 1 ? (
                    <div className="text-xs muted" style={{ marginBottom: 4 }}>
                      {j.title}
                    </div>
                  ) : null}
                  <Progress value={j.progress} indeterminate={j.state === 'queued'} />
                </div>
              ))}

            {/* 失败原因：**完整**原因，不是被省略号截断的那一行。
                逐条渲染：一条卡里两条任务时，挂掉的可能只有附带的那一条
                （主任务成功、文案被拦），只判主任务的话那条失败就查不到了。 */}
            {group
              .filter((j) => j.error)
              .map((j) => (
                <div className="task__error" key={j.id}>
                  <div className="task__error-head">
                    <Icon name="alert" size={14} />
                    <b>{errLabel(j.error!.code)}</b>
                    {/* 原码**留在展开体里**：短标签给人看，原码给排查用。
                        折叠行只印中文是为了让人读得懂，不是要把码藏起来 ——
                        展开体是「真相那一层」，出问题时客服与日志对的都是这个串。 */}
                    <span className="muted text-xs">
                      {j.error!.code} · 任务失败 · {fmtTime(j.finishedAt ?? j.createdAt)}
                    </span>
                  </div>
                  <p className="task__error-msg">{j.error!.message}</p>
                  {ERROR_HINT[j.error!.code] ? (
                    <p className="task__error-tip">建议：{ERROR_HINT[j.error!.code]}</p>
                  ) : null}
                  {/*
                    ⚠️ 「失败不扣费」**对取消不成立**（2026-10-10 修）。

                    取消在系统里就是 `state:'failed' + code:'ABORTED'`
                    （见 `JobCenterContext.cancel`），而这行字原先对所有失败
                    一视同仁地写「失败不扣费，可直接重试」—— 与 `ERROR_HINT.ABORTED`
                    那句「已经开跑的费用照扣」当场自相矛盾，两个说法还挨在同一张卡上。
                    实测依据见 `ERROR_HINT` 顶上那条注释（取消的 i2v 任务上游照跑完、扣了 152 币）。

                    所以按码分支：**只有取消这一支换说法**，其余照旧
                    —— 其余那些确实是在「上游没出东西」时失败，不扣费的说法没被推翻过。
                  */}
                  <div className="task__error-foot text-xs muted">
                    {j.error!.code === 'ABORTED'
                      ? '这条是你取消的，本机不再等它出片。⚠️ 取消拦不住上游：若已开跑，费用照扣；'
                      : '失败不扣费，可直接重试。'}
                    任务号 {j.serverJobId ?? '（未提交成功，无任务号）'}
                  </div>
                </div>
              ))}

            {media.length ? (
              <div className="task__album">
                <div className="task__album-head">
                  <Icon name="grid" size={14} />
                  <span>共 {media.length} 个结果 · 点击任意一张放大</span>
                  <span className="grow" />
                  <Button size="sm" variant="ghost" icon="folder" onClick={saveAllToLibrary}>
                    全部存素材
                  </Button>
                </div>
                <ResultGrid
                  outputs={media}
                  aspectRatio={aspectRatio ?? job.aspectRatio}
                  itemActions={itemActions}
                  bare
                />
              </div>
            ) : null}

            {texts.length ? (
              <div className="task__album">
                {texts.map(({ o, owner }, i) => (
                  <div key={`${owner.id}-${i}`} className="task__text">
                    <div className="task__album-head">
                      <Icon name="text" size={14} />
                      {/* 段头：单任务仍是「文案 1」（与改前逐字一致）；
                          一张卡里两条任务时改印**归属任务的真名** —— 否则图与文案
                          同框，「文案 1」是谁写的就得猜（主任务也可能有文本产出）。 */}
                      <span>{group.length > 1 ? owner.title : `文案 ${i + 1}`}</span>
                      <span className="grow" />
                      <CopyButton text={o.text ?? ''} />
                    </div>
                    <pre className="task__textbody">{o.text}</pre>
                  </div>
                ))}
              </div>
            ) : null}

            {audios.length ? (
              <div className="task__album">
                {/* 音频走 `AudioResult bare`：原生播放器 + 下载 + 存素材，
                    与生成页右栏那张卡**同一份实现**（自己在这儿画一个 <audio>
                    就是第二份，两边的下载/存素材按钮迟早对不上）。
                    `title` 即使在 `bare` 下不渲染也要传：它决定存进素材库的名字
                    与下载文件名，不传就全叫「语音结果 #1」。 */}
                <AudioResult outputs={audios} title={job.title} bare />
              </div>
            ) : null}

            {!media.length && !texts.length && !audios.length && !hasError ? (
              <div className="text-xs muted">这个任务没有产出。</div>
            ) : null}

            {/* 详情：出问题时要能自查，所以把可追溯字段全摊开。
                每行都按「有没有值」决定渲染 —— 作品图集没有耗时/消耗，
                硬塞一排「—」只是给用户增加阅读成本。 */}
            <div className="task__meta-grid">
              {job.serverJobId ? (
                <>
                  <span>任务号</span>
                  <span className="mono">{job.serverJobId}</span>
                </>
              ) : null}
              <span>模型</span>
              <span>{job.modelName ?? modelLabel(job.model)}</span>
              {job.aspectRatio || job.resolution ? (
                <>
                  <span>规格</span>
                  <span>
                    {[job.aspectRatio, job.resolution].filter(Boolean).join(' · ')}
                    {job.expectedOutputs > 1 ? ` · ${job.expectedOutputs} 个` : ''}
                  </span>
                </>
              ) : null}
              {elapsed > 0 ? (
                <>
                  <span>耗时</span>
                  <span>{fmtElapsed(elapsed)}</span>
                </>
              ) : null}
              {job.cost ? (
                <>
                  <span>消耗</span>
                  <span>¥{job.cost.toFixed(2)}</span>
                </>
              ) : null}
              {job.sourcePage ? (
                <>
                  <span>来源</span>
                  <span>{job.sourcePage}</span>
                </>
              ) : null}
            </div>

            {job.prompt ? (
              <details className="task__prompt">
                <summary className="text-xs muted" style={{ cursor: 'pointer' }}>
                  查看本次提交的提示词
                </summary>
                <div style={{ marginTop: 8 }}>
                  <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
                    <CopyButton text={job.prompt} label="复制提示词" />
                  </div>
                  <pre className="task__textbody">{job.prompt}</pre>
                </div>
              </details>
            ) : null}

            {/* 操作条：产出类动作都在这儿，与结果挨着；状态动作（取消/重试）在折叠头里 */}
            <div className="task__foot" onClick={(e) => e.stopPropagation()}>
              {inDrawer ? null : (
                <Button
                  size="sm"
                  variant="outline"
                  icon="expand"
                  title="在右侧展开这条任务：图更大、失败原因更好读，且不离开当前页"
                  onClick={() => setDetailOpen(true)}
                >
                  右侧展开
                </Button>
              )}
              {allOutputs.length ? (
                <Button size="sm" variant="outline" icon="download" loading={busy} onClick={() => void downloadAll()}>
                  下载全部（{allOutputs.length}）
                </Button>
              ) : null}
              {allOutputs.length ? (
                <Button size="sm" variant="outline" icon="folder" onClick={saveAllToLibrary}>
                  全部存素材
                </Button>
              ) : null}
              {/* 重试按**挂掉的那一条**给：一条卡里两条任务时，挂的可能只是附带的那条，
                  只判主任务的话那条失败就只能去历史里找入口了。
                  密度（`lean` / `dense`）与单任务时逐字一致 —— 见 `lean` prop 上的说明。 */}
              {group
                .filter((j) => j.state === 'failed')
                .map((j) => (
                  <Button
                    key={j.id}
                    size={lean ? 'icon' : 'sm'}
                    variant={lean ? 'ghost' : 'outline'}
                    icon="refresh"
                    title="按原参数重试"
                    aria-label={lean ? `重试：${j.title}` : undefined}
                    onClick={() => void retry(j.id)}
                  >
                    {lean ? null : group.length > 1 ? `重试 · ${j.title}` : '重试'}
                  </Button>
                ))}
              {!active ? (
                restore ? (
                  <Button
                    size="sm"
                    variant="outline"
                    icon="refresh"
                    title="恢复这条记录（内容一直在库里，恢复只是让它重新出现在列表里，不会重跑也不会再花钱）"
                    onClick={() => restore(job.id)}
                  >
                    恢复记录
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash"
                    title="清理这条记录（记录不会删除：可在任务中心「已清理」里恢复；已生成的文件仍在「我的作品」里）"
                    onClick={() => dismiss(job.id)}
                  >
                    移除记录
                  </Button>
                )
              ) : null}
              <span className="grow" />
              {actions}
            </div>
          </div>
        </CardBody>
      ) : null}
      </Card>

      <TaskDrawer
        job={job}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        aspectRatio={aspectRatio}
        itemActions={itemActions}
        actions={actions}
        restore={restore}
        onRedo={onRedo}
      />
    </>
  )
}
